
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  ArrowDown, 
  ArrowRight, 
  ArrowUp, 
  BadgeDollarSign,
  Download,
  ExternalLink, 
  FileText, 
  Heart, 
  Link,
  LineChart, 
  Plus, 
  RefreshCw, 
  RotateCw, 
  Settings, 
  Trash, 
  TrendingUp, 
  Users
} from 'lucide-react';
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Mock platform integration data
const platformIntegrations = [
  {
    id: 'patreon',
    name: 'Patreon',
    connected: true,
    lastSync: '2023-09-15T14:30:00Z',
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-100',
    supporterCount: 57,
    totalRevenue: 780,
    logo: 'https://cdn.worldvectorlogo.com/logos/patreon.svg'
  },
  {
    id: 'buymeacoffee',
    name: 'Buy Me A Coffee',
    connected: true,
    lastSync: '2023-09-14T10:15:00Z',
    iconColor: 'text-yellow-500',
    iconBg: 'bg-yellow-100',
    supporterCount: 23,
    totalRevenue: 230,
    logo: 'https://cdn.buymeacoffee.com/assets/img/guidelines/logo-mark-3.svg'
  },
  {
    id: 'kofi',
    name: 'Ko-fi',
    connected: false,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-100',
    logo: 'https://storage.ko-fi.com/cdn/brandasset/kofi_s_logo_nolabel.png'
  },
  {
    id: 'gumroad',
    name: 'Gumroad',
    connected: false,
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-100',
    logo: 'https://gumroad.com/favicon.ico'
  }
];

// Mock tiers data (now sourced from Patreon)
const mockTiers = [
  {
    id: '1',
    platform: 'patreon',
    name: 'Fan Club',
    price: 5,
    interval: 'monthly',
    benefits: [
      'Early access to new music',
      'Exclusive behind-the-scenes content',
      'Monthly livestream access'
    ],
    supporterCount: 32,
    conversionRate: 2.8,
    retentionRate: 92,
    totalRevenue: 160,
    image: 'https://source.unsplash.com/random/800x400/?music-fan',
    platformUrl: 'https://patreon.com/example/tiers/fanclub'
  },
  {
    id: '2',
    platform: 'patreon',
    name: 'Insider Circle',
    price: 15,
    interval: 'monthly',
    benefits: [
      'All Fan Club benefits',
      'Monthly video call with artist',
      'Name in album credits',
      'Exclusive merch discounts'
    ],
    supporterCount: 18,
    conversionRate: 1.5,
    retentionRate: 88,
    totalRevenue: 270,
    image: 'https://source.unsplash.com/random/800x400/?concert',
    platformUrl: 'https://patreon.com/example/tiers/insider'
  },
  {
    id: '3',
    platform: 'patreon',
    name: 'VIP Patron',
    price: 50,
    interval: 'monthly',
    benefits: [
      'All Insider Circle benefits',
      'Free ticket to one show per year',
      'Personalized birthday message',
      'Input on setlists',
      'Early merch access'
    ],
    supporterCount: 7,
    conversionRate: 0.6,
    retentionRate: 94,
    totalRevenue: 350,
    image: 'https://source.unsplash.com/random/800x400/?vip-backstage',
    platformUrl: 'https://patreon.com/example/tiers/vip'
  },
  {
    id: '4',
    platform: 'buymeacoffee',
    name: 'Coffee Supporter',
    price: 5,
    interval: 'one-time',
    benefits: [
      'Support my work',
      'Thank you message',
      'Name on supporters wall'
    ],
    supporterCount: 15,
    totalRevenue: 75,
    image: 'https://source.unsplash.com/random/800x400/?coffee',
    platformUrl: 'https://buymeacoffee.com/example'
  },
  {
    id: '5',
    platform: 'buymeacoffee',
    name: 'Premium Coffee',
    price: 15,
    interval: 'one-time',
    benefits: [
      'Support my work',
      'Personalized thank you',
      'Exclusive download',
      'Name on supporters wall'
    ],
    supporterCount: 8,
    totalRevenue: 120,
    image: 'https://source.unsplash.com/random/800x400/?coffee-shop',
    platformUrl: 'https://buymeacoffee.com/example/premium'
  }
];

// Mock supporters data (from various platforms)
const mockSupporters = [
  {
    id: '1',
    name: 'Jessica Lee',
    platform: 'patreon',
    tier: 'VIP Patron',
    since: '2023-05-12',
    total: 350,
    image: 'https://source.unsplash.com/random/100x100/?face-1',
    platformUrl: 'https://patreon.com/user/jessicalee'
  },
  {
    id: '2',
    name: 'Michael Taylor',
    platform: 'patreon',
    tier: 'Insider Circle',
    since: '2023-02-28',
    total: 165,
    image: 'https://source.unsplash.com/random/100x100/?face-2',
    platformUrl: 'https://patreon.com/user/michaeltaylor'
  },
  {
    id: '3',
    name: 'Sarah Johnson',
    platform: 'patreon',
    tier: 'Fan Club',
    since: '2023-07-04',
    total: 30,
    image: 'https://source.unsplash.com/random/100x100/?face-3',
    platformUrl: 'https://patreon.com/user/sarahjohnson'
  },
  {
    id: '4',
    name: 'David Rodriguez',
    platform: 'buymeacoffee',
    tier: 'Premium Coffee',
    since: '2023-06-19',
    total: 15,
    image: 'https://source.unsplash.com/random/100x100/?face-4',
    platformUrl: 'https://buymeacoffee.com/user/davidrodriguez'
  },
  {
    id: '5',
    name: 'Emma Wilson',
    platform: 'buymeacoffee',
    tier: 'Coffee Supporter',
    since: '2023-08-02',
    total: 5,
    image: 'https://source.unsplash.com/random/100x100/?face-5',
    platformUrl: 'https://buymeacoffee.com/user/emmawilson'
  }
];

const DirectArtistSupportHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState('integrations');
  const [selectedTier, setSelectedTier] = useState<any | null>(null);
  const [isConnectPlatformDialogOpen, setIsConnectPlatformDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<string>(new Date().toISOString());
  
  const connectedPlatforms = platformIntegrations.filter(platform => platform.connected);
  
  const totalSupporters = connectedPlatforms.reduce((sum, platform) => sum + (platform.supporterCount || 0), 0);
  const totalMonthlyRevenue = connectedPlatforms.reduce((sum, platform) => sum + (platform.totalRevenue || 0), 0);

  const handleSyncData = () => {
    // Mock sync function - would fetch data from connected platforms API
    setLastSyncTime(new Date().toISOString());
  };
  
  const handleConnectPlatform = (platformId: string) => {
    setSelectedPlatform(platformId);
    setIsConnectPlatformDialogOpen(true);
  };
  
  // Get platform color class based on platform ID
  const getPlatformColorClass = (platformId: string) => {
    const colorMap: {[key: string]: string} = {
      'patreon': 'bg-orange-100 text-orange-500',
      'buymeacoffee': 'bg-yellow-100 text-yellow-500',
      'kofi': 'bg-blue-100 text-blue-500',
      'gumroad': 'bg-purple-100 text-purple-500'
    };
    
    return colorMap[platformId] || 'bg-gray-100 text-gray-500';
  };
  
  return (
    <div className="p-6">
      <Tabs defaultValue="integrations" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
          <TabsList className="grid grid-cols-3 w-full md:w-auto">
            <TabsTrigger value="integrations">Platform Integrations</TabsTrigger>
            <TabsTrigger value="tiers">Supporter Tiers</TabsTrigger>
            <TabsTrigger value="supporters">Supporters</TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSyncData} className="gap-2">
              <RotateCw className="h-4 w-4" />
              <span>Sync Data</span>
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </Button>
          </div>
        </div>
        
        <TabsContent value="integrations" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold">Platform Integrations</h2>
            <p className="text-muted-foreground">
              Connect to platforms where fans support your music
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-muted/50 border shadow-sm">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Heart className="h-5 w-5 text-red-500" />
                      <h3 className="font-medium">Platforms Connected</h3>
                    </div>
                    <Badge variant="outline">{connectedPlatforms.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                      <h3 className="font-medium">Total Monthly Revenue</h3>
                    </div>
                    <Badge variant="outline">${totalMonthlyRevenue}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      <h3 className="font-medium">Total Supporters</h3>
                    </div>
                    <Badge variant="outline">{totalSupporters}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-5 w-5 text-purple-500" />
                      <h3 className="font-medium">Last Synced</h3>
                    </div>
                    <Badge variant="outline">
                      {new Date(lastSyncTime).toLocaleString()}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {platformIntegrations.map((platform) => (
              <Card 
                key={platform.id} 
                className={`border shadow-sm ${platform.connected ? '' : 'bg-muted/30'}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${platform.iconBg}`}>
                        {platform.logo ? (
                          <img 
                            src={platform.logo} 
                            alt={platform.name} 
                            className="w-8 h-8 object-contain" 
                          />
                        ) : (
                          <BadgeDollarSign className={`h-6 w-6 ${platform.iconColor}`} />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{platform.name}</CardTitle>
                        <div className="text-sm text-muted-foreground">
                          {platform.connected ? 'Connected' : 'Not Connected'}
                        </div>
                      </div>
                    </div>
                    <Switch checked={platform.connected} disabled={platform.connected} />
                  </div>
                </CardHeader>
                <CardContent>
                  {platform.connected ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-muted/50 p-3 rounded-lg">
                          <div className="text-sm text-muted-foreground">Supporters</div>
                          <div className="text-xl font-bold">{platform.supporterCount}</div>
                        </div>
                        <div className="bg-muted/50 p-3 rounded-lg">
                          <div className="text-sm text-muted-foreground">Revenue</div>
                          <div className="text-xl font-bold">${platform.totalRevenue}</div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Last synced: {new Date(platform.lastSync).toLocaleString()}
                      </div>
                      <div className="flex justify-between gap-2">
                        <Button variant="outline" size="sm" className="flex-1 gap-1">
                          <Settings className="h-3 w-3" />
                          <span>Settings</span>
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 gap-1">
                          <ExternalLink className="h-3 w-3" />
                          <span>Open</span>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Connect your {platform.name} account to import supporter data
                      </p>
                      <Button 
                        onClick={() => handleConnectPlatform(platform.id)}
                        className="w-full"
                      >
                        Connect {platform.name}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle>Platform Integration Benefits</CardTitle>
              <CardDescription>
                Connect to your existing support platforms to manage all your supporter data in one place
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  Muso360 doesn't replace your existing support platforms - it integrates with them to provide unified analytics and fan management.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Unified Dashboard</h3>
                  <p className="text-sm text-muted-foreground">
                    See all your supporter data across platforms in one place
                  </p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Cross-Platform Analytics</h3>
                  <p className="text-sm text-muted-foreground">
                    Compare performance metrics between different platforms
                  </p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Consolidated Supporter List</h3>
                  <p className="text-sm text-muted-foreground">
                    Identify fans who support you across multiple platforms
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Dialog 
            open={isConnectPlatformDialogOpen} 
            onOpenChange={setIsConnectPlatformDialogOpen}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  Connect to {platformIntegrations.find(p => p.id === selectedPlatform)?.name}
                </DialogTitle>
                <DialogDescription>
                  Link your account to import supporter data and analytics
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-center mb-4">
                  <div className={`w-16 h-16 rounded-md flex items-center justify-center ${platformIntegrations.find(p => p.id === selectedPlatform)?.iconBg || 'bg-gray-100'}`}>
                    {platformIntegrations.find(p => p.id === selectedPlatform)?.logo ? (
                      <img 
                        src={platformIntegrations.find(p => p.id === selectedPlatform)?.logo} 
                        alt={platformIntegrations.find(p => p.id === selectedPlatform)?.name} 
                        className="w-10 h-10 object-contain" 
                      />
                    ) : (
                      <BadgeDollarSign className={`h-8 w-8 ${platformIntegrations.find(p => p.id === selectedPlatform)?.iconColor || 'text-gray-500'}`} />
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">API Key / Access Token</label>
                  <Input className="mt-1" placeholder="Enter your access token" />
                  <p className="text-xs text-muted-foreground mt-1">
                    You can find this in your {platformIntegrations.find(p => p.id === selectedPlatform)?.name} account settings
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch id="autoSync" />
                  <label htmlFor="autoSync" className="text-sm">
                    Automatically sync data daily
                  </label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch id="importHistory" defaultChecked />
                  <label htmlFor="importHistory" className="text-sm">
                    Import historical data
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsConnectPlatformDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsConnectPlatformDialogOpen(false)}>
                  Connect Account
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        <TabsContent value="tiers" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Supporter Tiers</h2>
              <p className="text-muted-foreground">
                View and analyze your membership tiers from connected platforms
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="bg-muted/50 border shadow-sm">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Heart className="h-5 w-5 text-red-500" />
                      <h3 className="font-medium">Total Tiers</h3>
                    </div>
                    <Badge variant="outline">{mockTiers.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                      <h3 className="font-medium">Most Popular</h3>
                    </div>
                    <Badge variant="outline">Fan Club (32 patrons)</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BadgeDollarSign className="h-5 w-5 text-blue-500" />
                      <h3 className="font-medium">Highest Value</h3>
                    </div>
                    <Badge variant="outline">VIP Patron ($50/mo)</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-muted/50 border shadow-sm">
              <CardContent className="p-6">
                <h3 className="font-medium mb-3">Platform Distribution</h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        Patreon
                      </span>
                      <span>57 supporters (71%)</span>
                    </div>
                    <Progress value={71} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        Buy Me A Coffee
                      </span>
                      <span>23 supporters (29%)</span>
                    </div>
                    <Progress value={29} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockTiers.map((tier) => (
              <Card 
                key={tier.id} 
                className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedTier(tier)}
              >
                <div 
                  className="h-32 bg-cover bg-center rounded-t-lg" 
                  style={{ backgroundImage: `url(${tier.image})` }}
                >
                  <div className="w-full h-full bg-black/30 rounded-t-lg p-4 flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <Badge className={`self-start ${getPlatformColorClass(tier.platform)}`}>
                        {tier.interval === 'monthly' ? 'Monthly' : 'One-time'}
                      </Badge>
                      <Badge variant="outline" className="bg-black/70 text-white hover:bg-black/70 hover:text-white">
                        {tier.platform === 'patreon' ? 'Patreon' : 
                         tier.platform === 'buymeacoffee' ? 'Buy Me A Coffee' : 
                         tier.platform === 'kofi' ? 'Ko-fi' : 
                         tier.platform}
                      </Badge>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-white">{tier.name}</h3>
                      <div className="text-white/90">${tier.price}/{tier.interval === 'monthly' ? 'mo' : 'one-time'}</div>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Supporters</span>
                    <span className="font-medium">{tier.supporterCount}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Revenue</span>
                    <span className="font-medium">${tier.totalRevenue}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">
                    {tier.benefits.length} benefits offered
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <a href={tier.platformUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                        <ExternalLink className="h-3 w-3 mr-1" /> View on Platform
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {selectedTier && (
            <Card className="border shadow-sm mt-6">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTier(null)}>
                    ← Back
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getPlatformColorClass(selectedTier.platform)}>
                    {selectedTier.platform === 'patreon' ? 'Patreon' : 
                     selectedTier.platform === 'buymeacoffee' ? 'Buy Me A Coffee' : 
                     selectedTier.platform === 'kofi' ? 'Ko-fi' : 
                     selectedTier.platform}
                  </Badge>
                  <Button variant="outline" size="sm" asChild>
                    <a href={selectedTier.platformUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" /> View on Platform
                    </a>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div 
                      className="h-40 bg-cover bg-center rounded-lg mb-4" 
                      style={{ backgroundImage: `url(${selectedTier.image})` }}
                    >
                      <div className="w-full h-full bg-black/30 rounded-lg p-4 flex flex-col justify-end">
                        <h3 className="font-semibold text-xl text-white">{selectedTier.name}</h3>
                        <div className="text-white/90">${selectedTier.price}/{selectedTier.interval === 'monthly' ? 'mo' : 'one-time'}</div>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-lg mb-2">Benefits</h3>
                    <ul className="space-y-2">
                      {selectedTier.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="mt-1 text-green-500">✓</div>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Tier Performance</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <div className="text-sm text-muted-foreground mb-1">Supporters</div>
                          <div className="text-2xl font-bold">{selectedTier.supporterCount}</div>
                          <div className="flex items-center text-xs text-green-500 mt-1">
                            <ArrowUp className="h-3 w-3 mr-1" /> +4 this month
                          </div>
                        </div>
                        <div className="bg-muted/50 p-4 rounded-lg">
                          <div className="text-sm text-muted-foreground mb-1">Revenue</div>
                          <div className="text-2xl font-bold">${selectedTier.totalRevenue}</div>
                          <div className="flex items-center text-xs text-green-500 mt-1">
                            <ArrowUp className="h-3 w-3 mr-1" /> +$60 this month
                          </div>
                        </div>
                        {selectedTier.conversionRate && (
                          <div className="bg-muted/50 p-4 rounded-lg">
                            <div className="text-sm text-muted-foreground mb-1">Conversion Rate</div>
                            <div className="text-2xl font-bold">{selectedTier.conversionRate}%</div>
                            <div className="flex items-center text-xs text-muted-foreground mt-1">
                              of site visitors
                            </div>
                          </div>
                        )}
                        {selectedTier.retentionRate && (
                          <div className="bg-muted/50 p-4 rounded-lg">
                            <div className="text-sm text-muted-foreground mb-1">Retention</div>
                            <div className="text-2xl font-bold">{selectedTier.retentionRate ?? 'N/A'}%</div>
                            <div className="flex items-center text-xs text-muted-foreground mt-1">
                              monthly renewal rate
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Card className="border bg-muted/30">
                      <CardContent className="p-4 space-y-3">
                        <h3 className="font-semibold mb-2">Insights from Muso360</h3>
                        <div className="flex items-start gap-3">
                          <div className="bg-amber-100 p-1 rounded-full mt-0.5">
                            <LineChart className="h-4 w-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Most popular tier on your Patreon</p>
                            <p className="text-xs text-muted-foreground">32 patrons representing 56% of your total Patreon supporters</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="bg-green-100 p-1 rounded-full mt-0.5">
                            <TrendingUp className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Growth trend is positive</p>
                            <p className="text-xs text-muted-foreground">14% increase in patrons at this tier in the last 3 months</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Button className="w-full" variant="outline" asChild>
                      <a href={selectedTier.platformUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" /> Manage Tier on {selectedTier.platform === 'patreon' ? 'Patreon' : 
                        selectedTier.platform === 'buymeacoffee' ? 'Buy Me A Coffee' : 
                        selectedTier.platform === 'kofi' ? 'Ko-fi' : 
                        selectedTier.platform}
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="supporters" className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold">Your Supporters</h2>
            <p className="text-muted-foreground">
              View all supporters across connected platforms
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border shadow-sm col-span-1">
              <CardHeader className="pb-2">
                <CardTitle>Supporter Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="text-xl font-bold">{totalSupporters}</div>
                  <div className="text-sm text-muted-foreground">Total Supporters</div>
                  <div className="flex items-center text-xs text-green-500">
                    <ArrowUp className="h-3 w-3 mr-1" /> +7 in the last month
                  </div>
                </div>
                
                <div className="pt-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Platform Distribution</span>
                  </div>
                  {connectedPlatforms.map((platform) => (
                    <div key={platform.id} className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${platform.iconColor.replace('text-', 'bg-')}`}></div>
                          {platform.name}
                        </span>
                        <span>
                          {platform.supporterCount} ({Math.round((platform.supporterCount / totalSupporters) * 100)}%)
                        </span>
                      </div>
                      <Progress value={(platform.supporterCount / totalSupporters) * 100} className="h-1.5" />
                    </div>
                  ))}
                </div>
                
                <div className="pt-2">
                  <div className="text-sm font-medium mb-2">Export Options</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <FileText className="h-3 w-3 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm">
                      <Download className="h-3 w-3 mr-1" /> Full Data
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border shadow-sm col-span-1 md:col-span-2">
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle>Supporter List</CardTitle>
                <div className="flex items-center gap-2">
                  <Input className="h-8 w-[180px]" placeholder="Search supporters..." />
                  <select className="h-8 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <option value="all">All Platforms</option>
                    {connectedPlatforms.map((platform) => (
                      <option key={platform.id} value={platform.id}>{platform.name}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {mockSupporters.map((supporter) => (
                    <div key={supporter.id} className="p-4 hover:bg-muted/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={supporter.image} />
                          <AvatarFallback>{supporter.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{supporter.name}</div>
                          <div className="text-sm text-muted-foreground">
                            Since {new Date(supporter.since).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <Badge className={getPlatformColorClass(supporter.platform)}>
                            {supporter.platform === 'patreon' ? 'Patreon' : 
                             supporter.platform === 'buymeacoffee' ? 'Buy Me A Coffee' : 
                             supporter.platform === 'kofi' ? 'Ko-fi' : 
                             supporter.platform}
                          </Badge>
                          <div className="flex items-center justify-end gap-1 text-sm text-muted-foreground mt-1">
                            <span>${supporter.total} total</span>
                            <span>•</span>
                            <span>{supporter.tier}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                          <a 
                            href={supporter.platformUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span className="sr-only md:not-sr-only">View</span>
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="border-t justify-between py-3">
                <div className="text-sm text-muted-foreground">
                  Showing 5 of {totalSupporters} supporters
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm">
                    Next
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>
          
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle>Integration Tips</CardTitle>
              <CardDescription>
                Make the most of your platform integrations with these suggestions
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="bg-orange-100 p-3 rounded-full mb-3">
                      <Link className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold">Cross-Link Platforms</h3>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">
                      Add links to all your support platforms on each platform
                    </p>
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <a 
                        href="https://patreon.com/settings" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Manage Links
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="bg-blue-100 p-3 rounded-full mb-3">
                      <BadgeDollarSign className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold">Optimize Tier Pricing</h3>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">
                      Use cross-platform analytics to find optimal price points
                    </p>
                    <Button variant="outline" size="sm" className="w-full">
                      View Analytics
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="bg-green-100 p-3 rounded-full mb-3">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold">Diversify Platforms</h3>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">
                      Connect to multiple support platforms to reach more fans
                    </p>
                    <Button variant="outline" size="sm" className="w-full" onClick={() => setActiveTab('integrations')}>
                      Add Platform
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DirectArtistSupportHub;
